import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Eye, EyeOff, Lock, CheckCircle, Loader2 } from "lucide-react";
import { resetPassword } from "../lib/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [passwords, setPasswords] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!passwords.newPassword) {
      toast.error("Veuillez entrer un nouveau mot de passe");
      return;
    }
    if (passwords.newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    
    setIsLoading(true);
    try {
      await resetPassword(token, passwords.newPassword);
      setSuccess(true);
      toast.success("Mot de passe réinitialisé avec succès");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la réinitialisation");
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F9F7F5] via-[#F0EBE6] to-[#F9F7F5] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/80 backdrop-blur shadow-xl border-[#E5E0DC]">
          <CardContent className="pt-6">
            <div className="text-center">
              <Lock className="w-12 h-12 text-status-error mx-auto mb-4" />
              <h2 className="font-heading text-xl text-secondary mb-2">Lien invalide</h2>
              <p className="text-muted-foreground mb-6">
                Ce lien de réinitialisation est invalide ou a expiré.
              </p>
              <Link to="/login">
                <Button className="bg-primary hover:bg-primary-hover">
                  Retour à la connexion
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F9F7F5] via-[#F0EBE6] to-[#F9F7F5] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/80 backdrop-blur shadow-xl border-[#E5E0DC]">
          <CardContent className="pt-6">
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-status-success mx-auto mb-4" />
              <h2 className="font-heading text-xl text-secondary mb-2">Mot de passe modifié</h2>
              <p className="text-muted-foreground mb-6">
                Votre mot de passe a été réinitialisé avec succès.
              </p>
              <Link to="/login">
                <Button className="bg-primary hover:bg-primary-hover">
                  Se connecter
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F9F7F5] via-[#F0EBE6] to-[#F9F7F5] flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-lg mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_fleet-manager-378/artifacts/sum73cxm_image.png" 
              alt="Food Truck United" 
              className="w-16 h-16 object-contain"
            />
          </div>
          <h1 className="font-heading text-3xl font-black text-primary">Food Truck United</h1>
        </div>

        <Card className="bg-white/80 backdrop-blur shadow-xl border-[#E5E0DC]">
          <CardHeader className="text-center">
            <CardTitle className="font-heading text-2xl text-secondary">
              Nouveau mot de passe
            </CardTitle>
            <CardDescription>
              Choisissez un nouveau mot de passe pour votre compte
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="new-password">Nouveau mot de passe</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={passwords.newPassword}
                    onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                    className="pl-10 pr-10 h-12 border-[#E5E0DC]"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Minimum 6 caractères</p>
              </div>

              <div>
                <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={passwords.confirmPassword}
                    onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                    className="pl-10 h-12 border-[#E5E0DC]"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-primary hover:bg-primary-hover text-white font-semibold"
                disabled={isLoading}
                data-testid="reset-password-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Réinitialiser le mot de passe"
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-[#E5E0DC] text-center">
              <Link to="/login" className="text-sm text-primary hover:underline">
                Retour à la connexion
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
